import React, { useState, useEffect } from 'react';
import { X, Sparkle, CheckCircle, Spinner, Copy, ArrowRight, Lightning, Calendar, Hash, PenNib } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { useFormDefaults } from '@/store/usePayGBotContext';
import OutputActions from './OutputActions';
import BusinessMemoryBanner from './BusinessMemoryBanner';

// Map saved business_memory.tone_of_voice → the Content Pack content_style
// options so the user's saved tone picks the right preset by default.
const TONE_TO_STYLE = {
  friendly: 'entertaining',
  casual: 'entertaining',
  playful: 'entertaining',
  humorous: 'entertaining',
  professional: 'educational',
  expert: 'educational',
  educational: 'educational',
  authoritative: 'educational',
  inspirational: 'inspiring',
  motivational: 'inspiring',
  inspiring: 'inspiring',
  storyteller: 'storytelling',
  storytelling: 'storytelling',
  personal: 'storytelling',
  salesy: 'promotional',
  promotional: 'promotional',
};

const mapToneToStyle = (tone) => {
  if (!tone || typeof tone !== 'string') return null;
  const key = tone.trim().toLowerCase();
  if (TONE_TO_STYLE[key]) return TONE_TO_STYLE[key];
  for (const [needle, style] of Object.entries(TONE_TO_STYLE)) {
    if (key.includes(needle)) return style;
  }
  return null;
};

const ContentPackModal = ({ isOpen, onClose, onSuccess, onPlanWeek, selectedIdea = null }) => {
  const user = getUser();
  const formDefaults = useFormDefaults();

  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedPost, setExpandedPost] = useState(0);
  const [memory, setMemory] = useState(null);

  const [formData, setFormData] = useState({
    niche: '',
    content_theme: '',
    platform: 'tiktok',
    content_style: 'educational',
    post_count: 5
  });

  // Load business memory when the modal opens so we can prefill niche,
  // theme, platform, and style from the user's single saved profile.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/business-memory/profile');
        const mem = res.data?.memory || res.data || null;
        if (!cancelled) setMemory(mem);
      } catch {
        if (!cancelled) setMemory(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Prefill form fields from (in priority order): selectedIdea → business
  // memory → legacy formDefaults/user. Memory wins over blank/generic input.
  useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => {
      const nicheFromMemory = memory?.niche || formDefaults.niche || user?.niche || '';
      const platformRaw =
        selectedIdea?.platform ||
        memory?.selected_platform ||
        formDefaults.platform ||
        user?.platform ||
        prev.platform;
      const platform = (platformRaw || 'tiktok').toString().toLowerCase().replace(/\s+/g, '_');
      const themeFromIdea = selectedIdea?.topic || selectedIdea?.title || selectedIdea?.idea || '';
      const themeFromMemory = memory?.content_focus || '';
      const style = mapToneToStyle(memory?.tone_of_voice) || prev.content_style;
      return {
        ...prev,
        niche: prev.niche && prev.niche.trim() ? prev.niche : nicheFromMemory,
        content_theme: prev.content_theme && prev.content_theme.trim()
          ? prev.content_theme
          : (themeFromIdea || themeFromMemory || prev.content_theme),
        platform: platform || prev.platform,
        content_style: style,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, memory, selectedIdea, formDefaults.niche, formDefaults.platform, user?.niche, user?.platform]);

  const platforms = [
    { id: 'tiktok', label: 'TikTok' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'youtube_shorts', label: 'YouTube Shorts' },
    { id: 'twitter', label: 'X (Twitter)' }
  ];

  const contentStyles = [
    { id: 'educational', label: 'Educational', desc: 'Teach something valuable' },
    { id: 'entertaining', label: 'Entertaining', desc: 'Make them laugh or smile' },
    { id: 'inspiring', label: 'Inspiring', desc: 'Motivate and uplift' },
    { id: 'storytelling', label: 'Storytelling', desc: 'Share experiences' },
    { id: 'promotional', label: 'Promotional', desc: 'Sell your offer' }
  ];

  const handleSubmit = async () => {
    if (!formData.niche) {
      toast.error('Please enter your niche');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/content/generate-pack', {
        niche: formData.niche,
        content_theme: formData.content_theme || `${formData.niche} tips and insights`,
        platform: formData.platform,
        content_style: formData.content_style,
        post_count: formData.post_count,
        selected_idea: selectedIdea || undefined,
      });
      
      setResult(response.data);
      setStep('result');
      
      if (onSuccess) onSuccess();
      toast.success('Your content pack is ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content pack');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setExpandedPost(0);
    onClose();
  };

  const copyAllContent = () => {
    if (!result?.posts) return;
    
    const allContent = result.posts.map((post, i) => 
      `POST ${i + 1}\n\nHook: ${post.hook}\n\nScript:\n${post.script}\n\nCaption:\n${post.caption}\n\nHashtags: ${post.hashtags}\n\n${'='.repeat(40)}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(allContent);
    toast.success('All posts copied!');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Sparkle size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Generate Content Pack</h2>
                <p className="text-sm text-slate-400">5-7 ready-to-post pieces of content</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                {/* Business Memory Reassurance */}
                <BusinessMemoryBanner />

                {/* Selected viral idea reminder (carried over from Find Viral Ideas) */}
                {selectedIdea && (selectedIdea.topic || selectedIdea.hook) && (
                  <div
                    className="flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-400/30 bg-amber-500/10 text-amber-100 text-xs"
                    data-testid="content-pack-selected-idea"
                  >
                    <Lightning size={14} weight="fill" className="text-amber-300 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Building from your selected viral idea</p>
                      {selectedIdea.topic && <p className="opacity-80 truncate">Topic: {selectedIdea.topic}</p>}
                      {selectedIdea.hook && <p className="opacity-80 truncate">Hook: {selectedIdea.hook}</p>}
                    </div>
                  </div>
                )}

                {/* Niche */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What's your niche?
                  </label>
                  <input
                    type="text"
                    value={formData.niche}
                    onChange={(e) => setFormData({...formData, niche: e.target.value})}
                    placeholder="e.g., Fitness, Personal Finance, Productivity"
                    data-testid="content-pack-niche"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Content Theme (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Content theme (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.content_theme}
                    onChange={(e) => setFormData({...formData, content_theme: e.target.value})}
                    placeholder="e.g., Morning routines, Money mistakes to avoid"
                    data-testid="content-pack-theme"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Platform */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Platform
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {platforms.map((platform) => (
                      <button
                        key={platform.id}
                        onClick={() => setFormData({...formData, platform: platform.id})}
                        className={`p-3 rounded-lg text-center transition-all ${
                          formData.platform === platform.id
                            ? 'bg-purple-500/20 border-purple-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.platform === platform.id ? 'text-purple-400' : 'text-white'}`}>
                          {platform.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Style */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Content style
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {contentStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setFormData({...formData, content_style: style.id})}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.content_style === style.id
                            ? 'bg-purple-500/20 border-purple-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium block ${formData.content_style === style.id ? 'text-purple-400' : 'text-white'}`}>
                          {style.label}
                        </span>
                        <span className="text-xs text-slate-500">{style.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Post Count */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    How many posts?
                  </label>
                  <div className="flex space-x-2">
                    {[5, 6, 7].map((count) => (
                      <button
                        key={count}
                        onClick={() => setFormData({...formData, post_count: count})}
                        className={`flex-1 p-3 rounded-lg text-center transition-all ${
                          formData.post_count === count
                            ? 'bg-purple-500/20 border-purple-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.post_count === count ? 'text-purple-400' : 'text-white'}`}>
                          {count} posts
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="content-pack-submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Generate My Content Pack</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Creating Your Content Pack</h3>
                <p className="text-slate-400">Generating {formData.post_count} scroll-stopping posts for {formData.platform}...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle size={20} className="text-purple-400" weight="fill" />
                    <span className="text-purple-400 font-semibold">{result.posts?.length || formData.post_count} Posts Ready!</span>
                  </div>
                  <p className="text-white text-sm">Your content pack for {formData.niche} on {formData.platform}</p>
                </div>

                {/* Copy All Button */}
                <button
                  onClick={copyAllContent}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors flex items-center justify-center space-x-2"
                >
                  <Copy size={18} />
                  <span>Copy All Posts</span>
                </button>

                {/* Posts */}
                <div className="space-y-3">
                  {result.posts?.map((post, i) => (
                    <div 
                      key={i} 
                      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
                      data-testid={`content-pack-post-${i}`}
                    >
                      {/* Post Header - Always visible */}
                      <button
                        onClick={() => setExpandedPost(expandedPost === i ? -1 : i)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <span className="text-purple-400 font-bold text-sm">{i + 1}</span>
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{post.topic || `Post ${i + 1}`}</p>
                            <p className="text-slate-400 text-xs truncate max-w-[200px] sm:max-w-md">{post.hook}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs ${expandedPost === i ? 'text-purple-400' : 'text-slate-500'}`}>
                            {expandedPost === i ? 'Hide' : 'View'}
                          </span>
                        </div>
                      </button>

                      {/* Expanded Content */}
                      {expandedPost === i && (
                        <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
                          {/* Hook */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center space-x-1">
                                <Lightning size={12} className="text-yellow-400" />
                                <span>Hook</span>
                              </label>
                              <button
                                onClick={() => handleCopy(post.hook, `post-${i}-hook`)}
                                className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                              >
                                {copiedField === `post-${i}-hook` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-purple-400 bg-purple-500/10 p-3 rounded-lg text-sm font-medium">{post.hook}</p>
                          </div>

                          {/* Script */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center space-x-1">
                                <PenNib size={12} />
                                <span>Script</span>
                              </label>
                              <button
                                onClick={() => handleCopy(post.script, `post-${i}-script`)}
                                className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                              >
                                {copiedField === `post-${i}-script` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-slate-300 bg-white/5 p-3 rounded-lg text-sm whitespace-pre-wrap">{post.script}</p>
                          </div>

                          {/* Caption */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Caption</label>
                              <button
                                onClick={() => handleCopy(post.caption, `post-${i}-caption`)}
                                className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                              >
                                {copiedField === `post-${i}-caption` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-slate-300 bg-white/5 p-3 rounded-lg text-sm">{post.caption}</p>
                          </div>

                          {/* Hashtags */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center space-x-1">
                                <Hash size={12} />
                                <span>Hashtags</span>
                              </label>
                              <button
                                onClick={() => handleCopy(post.hashtags, `post-${i}-hashtags`)}
                                className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                              >
                                {copiedField === `post-${i}-hashtags` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-blue-400 bg-blue-500/10 p-3 rounded-lg text-sm">{post.hashtags}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Output Actions - Copy, Download PDF, Save */}
                <div className="pt-4 border-t border-white/10">
                  <OutputActions
                    content={result.posts?.map((p, i) => `POST ${i+1}\nHook: ${p.hook}\nScript: ${p.script}\nCaption: ${p.caption}\nHashtags: ${p.hashtags}`).join('\n\n---\n\n')}
                    pdfType="content_pack"
                    pdfData={{
                      posts: result.posts?.map(p => ({
                        hook: p.hook,
                        script: p.script,
                        caption: p.caption,
                        hashtags: p.hashtags
                      }))
                    }}
                    saveType="content_pack"
                    saveTitle={`Content Pack - ${formData.niche}`}
                    saveContent={{
                      niche: formData.niche,
                      platform: formData.platform,
                      content_style: formData.content_style,
                      posts: result.posts
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-4">
                  {/* Primary CTA - Plan Week */}
                  <button
                    onClick={() => {
                      onPlanWeek?.();
                      handleClose();
                    }}
                    data-testid="plan-week-btn"
                    className="w-full py-3.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                  >
                    <Calendar size={20} weight="fill" />
                    <span>Plan My Week</span>
                    <ArrowRight size={18} />
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setStep('input'); setResult(null); }}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Create New Pack
                    </button>
                    <button
                      onClick={handleClose}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ContentPackModal;
